<template>
    <model-ajax-form :model-config="props.modelConfig"
                     :model="props.model"
                     :parent-model="props.parentModel"
                     :confirm="props.confirm"
                     :data="props.data"
                     :disabled="props.disabled"
                     :edit="props.edit"
                     :success-message-resolver="props.successMessageResolver"
					 :prevent-success-action="props.preventSuccessAction"
                     v-model:valid="isValid"
                     v-model:loading="isLoading"
					 ref="formElement"
                     @success="(data, response) => $emit('success', data, response)"
                     @reset="$emit('reset')">

        <card xs-tile :flat="props.flat">
            <card-title v-if="pageTitle" class="none sm:flex">
				<div class="flex flex-column overflow-hidden">
					<breadcrumbs v-if="pageBreadcrumbs.length" :items="pageBreadcrumbs" small class="mb-8 font-text font-regular"/>
					<span>{{ pageTitle }}</span>
				</div>
            </card-title>
            <lazy-card-subtitle v-if="pageDescription" small class="pt-24 sm:pt-4">{{ pageDescription }}</lazy-card-subtitle>
            <card-text :class="props.noPadding ? 'p-0' : 'pt-8 pb-24'" fill>
                <slot/>
            </card-text>
        </card>
        <template v-if="$slots.button" #button>
            <slot name="button"/>
        </template>

        <template v-if="$slots.buttons" #buttons>
            <slot name="buttons"/>
        </template>
    </model-ajax-form>

</template>

<script setup lang="ts">
import {ModelConfig} from "../../composables/useModelConfig";
import {Model} from "@spoferan/spoferan-ts-core";

const props = withDefaults(defineProps<{
	modelConfig: ModelConfig,

	// The model to display the details for
	model?: Model | null, // Might be null when fetch loaded

	// The parent model of the model the displayed page belongs to
	parentModel?: Model,

	// The display and navigation data of the model's parent model. Should be a direct child of the model.
	parentModelConfig?: ModelConfig,

	// The data to send to create or update the model
	data?: {},

	// A custom page title
	title?: string,

	// A custom page description
	description?: string,

	// The confirmation message to show before submitting the form
	confirm?: {},

	// Whether the required data for the page, e.g., the model, is currently being loaded
	pending?: boolean,

	// Whether updates are disabled
	disabled?: boolean,

	// Whether the form is used to update the model
	edit?: boolean,

	// The valid prop to keep in sync with the form's valid prop
	valid?: boolean,

	// The loading prop to keep in sync with the form's loading prop
	loading?: boolean,

	// The error that might occurred while fetching the required data for the page
	error?: Object,

	// The function to refresh the required data for the page
	refresh?: Function,

	// A function that receives the response data and response object to resolve an additional success message to display when the request was successful.
	// Example: (data, response) => t('success')
	successMessageResolver?: (data: {}, response: {}) => string,

	// Whether to prevent displaying the success message or applying a redirect for edits after a successful request
	preventSuccessAction?: boolean,

	// Whether the form shall not be shown within a card
	flat?: boolean,

	// Whether the page shall be shown in full width
	fluid?: boolean,

	// Whether the content shall receive no padding
	noPadding?: boolean,

	// Whether to hide the page description
	hideDescription?: boolean,

	// The breadcrumbs to display before the auto generated breadcrumbs
	prefixBreadcrumbs?: {}[],

	// The breadcrumbs to display after the auto generated breadcrumbs
	suffixBreadcrumbs?: {}[],

}>(), {
	prefixBreadcrumbs: [],
	suffixBreadcrumbs: [],
});

const emit = defineEmits(['success', 'reset', 'update:valid', 'update:loading']);

const {t} = useI18n();

const formElement = ref(null);

const isValid = ref(props.valid);
watch(isValid, (newValue) => {
	emit('update:valid', newValue)
});

const isLoading = ref(props.loading);
watch(isLoading, (newValue) => {
	emit('update:loading', newValue)
});

const pageTitle = computed(() => {
	return props.title ?? t(`view.my.profile.manage.${props.modelConfig.config_key}.${props.edit ? 'edit' : 'create'}.title`, props.modelConfig.lang_params_resolver(props.model, props.parentModel))
});

const pageDescription = computed(() => {
	return !props.description && !props.hideDescription ? t(`view.my.profile.manage.${props.modelConfig.config_key}.${props.edit ? 'edit' : 'create'}.description`, props.modelConfig.lang_params_resolver(props.model, props.parentModel)) : props.description
});

const pageBreadcrumbs = computed(() => {
	const breadcrumbs = [...props.prefixBreadcrumbs];

	if (props.parentModel && props.parentModelConfig) {
		breadcrumbs.push({
				text: t(`view.my.profile.manage.${props.parentModelConfig.config_key}.index.title`, props.parentModelConfig.lang_params_resolver(props.parentModel)),
				to: props.parentModelConfig.index_route_resolver ? props.parentModelConfig.index_route_resolver() : null
			},
			{
				text: props.parentModelConfig.name_resolver(props.parentModel),
				to: props.parentModelConfig.show_route_resolver ? props.parentModelConfig.show_route_resolver(props.parentModel) : null
			})
	}

	breadcrumbs.push(...props.suffixBreadcrumbs);

	// Display the index part of the model only when an index page exists as otherwise it's just a pseudo-model without the need of displaying an index breadcrumb
	const indexRoute = props.modelConfig.index_route_resolver ? props.modelConfig.index_route_resolver(props.parentModel) : null;
	if (indexRoute) {
		breadcrumbs.push({
			text: t(`view.my.profile.manage.${props.modelConfig.config_key}.index.title`, props.modelConfig.lang_params_resolver(props.parentModel)),
			to: indexRoute
		});
	}

	if (props.edit && props.model) {
		const modelName = props.modelConfig.name_resolver(props.model);
		if (modelName) {
			breadcrumbs.push({
				text: modelName,
				to: props.modelConfig.show_route_resolver ? props.modelConfig.show_route_resolver(props.model, props.parentModel) : null,
			})
		}
	}

	return breadcrumbs;
});

async function submit() {
	if (formElement.value) {
		return formElement.value.submit();
	}
}

defineExpose({
	submit,
});

// Note that this only works on client side as used within a non-page component
useMeta({
	title: pageTitle.value,
	description: pageDescription.value
})

</script>